import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Upload,
  Modal,
  App,
  Table,
  Divider,
  Empty,
  Spin,
  Dropdown,
  Input
} from 'antd';
import {
  ArrowLeftOutlined,
  UploadOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { AuthContext } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/rolePermissions';
import dayjs from 'dayjs';
import archiveService from '../../services/archiveService';
import FileViewerDrawer from './FileViewerDrawer';
import logger from '../../utils/logger';

const { Title, Text } = Typography;

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { currentUser } = useContext(AuthContext);

  // Role-based permissions
  const userRole = currentUser?.role;
  const canUpload = userRole === USER_ROLES.TENANT_ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
  const canDelete = userRole === USER_ROLES.TENANT_ADMIN || userRole === USER_ROLES.SUPER_ADMIN;

  // State
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Delete confirmation modals
  const [deleteProjectModalOpen, setDeleteProjectModalOpen] = useState(false);
  const [deleteFileModalOpen, setDeleteFileModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Project name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  // File search/filter
  const [searchText, setSearchText] = useState('');

  // Fetch project details
  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const data = await archiveService.getAllProjects();
      const foundProject = data.find(p => p._id === projectId);

      if (!foundProject) {
        message.error('Project not found');
        navigate('/archive');
        return;
      }

      setProject(foundProject);
    } catch (err) {
      logger.error('Error fetching project:', err);
      message.error('Failed to load project');
      navigate('/archive');
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleUpload = async (info) => {
    try {
      setUploadLoading(true);

      const uploadPromises = info.fileList.map(file =>
        archiveService.uploadDocument('project', projectId, file.originFileObj)
      );

      await Promise.all(uploadPromises);

      message.success(`Uploaded ${info.fileList.length} file(s) - Processing...`, 3);
      setFileList([]);

      // Immediate refresh to show uploaded files
      await fetchProject();

      // Poll for processing completion
      const pollInterval = 2000; // Check every 2 seconds
      const maxPolls = 30; // Max 60 seconds (30 * 2s)
      let pollCount = 0;

      const checkProcessingStatus = async () => {
        const response = await archiveService.getAllProjects();
        const currentProject = response.find(p => p.id === projectId);

        if (!currentProject) return true; // Stop polling if project not found

        const pendingDocs = currentProject.documents?.filter(
          doc => doc.processing_status === 'pending'
        ) || [];

        if (pendingDocs.length === 0) {
          // All files processed
          await fetchProject();
          message.success(`Processing complete - All ${info.fileList.length} file(s) ready!`, 3);
          return true; // Stop polling
        }

        pollCount++;
        if (pollCount >= maxPolls) {
          // Timeout - stop polling but inform user
          await fetchProject();
          message.warning(`Some files still processing (${pendingDocs.length} remaining). Refresh page to check status.`, 5);
          return true; // Stop polling
        }

        return false; // Continue polling
      };

      // Start polling
      const pollTimer = setInterval(async () => {
        const shouldStop = await checkProcessingStatus();
        if (shouldStop) {
          clearInterval(pollTimer);
        }
      }, pollInterval);

    } catch (err) {
      logger.error('Error uploading files:', err);
      message.error('Failed to upload one or more files');
    } finally {
      setUploadLoading(false);
    }
  };

  // Open delete file modal
  const openDeleteFileModal = (file) => {
    setFileToDelete(file);
    setDeleteFileModalOpen(true);
  };

  // Handle file delete confirmation
  const handleDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      await archiveService.deleteDocument(projectId, fileToDelete._id);
      message.success(`Deleted "${fileToDelete.filename}"`);
      setDeleteFileModalOpen(false);
      setFileToDelete(null);
      fetchProject(); // Refresh project data
    } catch (err) {
      logger.error('Error deleting file:', err);
      message.error('Failed to delete file');
    }
  };

  // Handle file view
  const handleViewFile = (file) => {
    setSelectedFile(file);
    setDrawerOpen(true);
  };

  // Handle file switch in drawer
  const handleFileSwitch = (file) => {
    setSelectedFile(file);
  };

  // Handle delete project confirmation
  const handleDeleteProject = async () => {
    try {
      await archiveService.deleteProject(projectId);
      message.success(`Deleted project "${project.name}"`);
      setDeleteProjectModalOpen(false);
      navigate('/archive');
    } catch (err) {
      logger.error('Error deleting project:', err);
      message.error('Failed to delete project');
    }
  };

  // Handle project name edit
  const startEditingName = () => {
    setEditedName(project.name);
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const saveProjectName = async () => {
    if (!editedName.trim() || editedName === project.name) {
      setIsEditingName(false);
      return;
    }

    try {
      await archiveService.updateProject(projectId, {
        name: editedName,
        description: project.description || ''
      });

      message.success('Project name updated');
      setIsEditingName(false);
      fetchProject(); // Refresh to get updated data
    } catch (err) {
      logger.error('Error updating project name:', err);
      message.error('Failed to update project name');
    }
  };

  // Upload props
  const uploadProps = {
    multiple: true,
    accept: '.pdf,.doc,.docx,.ppt,.pptx',
    fileList: fileList,
    beforeUpload: (file) => {
      const allowedTypes = [
        'application/pdf',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const allowedExtensions = ['.pdf', '.ppt', '.pptx', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        message.error(`${file.name} is not a valid file type`);
        return Upload.LIST_IGNORE;
      }

      return false; // Prevent auto upload
    },
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    onRemove: (file) => {
      setFileList(fileList.filter(f => f.uid !== file.uid));
    }
  };

  // Get embedding status
  const getProcessingStatus = (status) => {
    switch (status) {
      case 'ready':
        return { icon: <CheckCircleOutlined />, color: 'success', text: 'Ready' };
      case 'failed':
        return { icon: <CloseCircleOutlined />, color: 'error', text: 'Failed' };
      case 'pending':
      default:
        return { icon: <ClockCircleOutlined />, color: 'processing', text: 'Processing' };
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" tip="Loading project..." />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: '24px' }}>
        <Empty description="Project not found" />
      </div>
    );
  }

  const documents = project.documents || [];

  // Filter documents based on search
  const filteredDocuments = searchText
    ? documents.filter(doc =>
        doc.filename?.toLowerCase().includes(searchText.toLowerCase())
      )
    : documents;

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Top Action Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        {/* Left: Back Button */}
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/archive')}
        >
          Back to Archive
        </Button>

        {/* Right: Action Buttons */}
        <Space>
          {canUpload && (
            <Upload
              {...uploadProps}
              showUploadList={false}
              onChange={handleUpload}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={uploadLoading}
              >
                Add Files
              </Button>
            </Upload>
          )}
          {canDelete && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteProjectModalOpen(true)}
            >
              Delete Project
            </Button>
          )}
        </Space>
      </div>

      {/* Project Title Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        {/* Left: Editable Project Title */}
        <div style={{ flex: 1 }}>
          {isEditingName ? (
            <Space>
              <Input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onPressEnter={saveProjectName}
                style={{ width: 400, fontSize: '24px', fontWeight: 600 }}
                autoFocus
              />
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={saveProjectName}
              />
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={cancelEditingName}
              />
            </Space>
          ) : (
            <Space>
              <Title level={2} style={{ margin: 0 }}>
                {project.name}
              </Title>
              {canUpload && (
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={startEditingName}
                />
              )}
            </Space>
          )}
        </div>

        {/* Right: Metadata */}
        <Space split={<Divider type="vertical" />}>
          <Text type="secondary">
            Created: {dayjs(project.created_at).format('MMM D, YYYY h:mm A')}
          </Text>
          <Text type="secondary">
            Last Updated: {dayjs(project.updated_at || project.created_at).fromNow()}
          </Text>
        </Space>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search files..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 400 }}
          allowClear
        />
      </div>

      {/* Documents Table */}
      <Card bodyStyle={{ padding: 0 }}>
        {documents.length === 0 ? (
          <Empty
            description="No files uploaded yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '40px 0' }}
          >
            <Upload {...uploadProps} onChange={handleUpload}>
              <Button type="primary" icon={<UploadOutlined />} loading={uploadLoading}>
                Upload Your First File
              </Button>
            </Upload>
          </Empty>
        ) : (
          <Table
            dataSource={filteredDocuments}
            rowKey="_id"
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} files${searchText ? ` (filtered from ${documents.length})` : ''}`
            }}
            onRow={(record) => ({
              onClick: () => handleViewFile(record),
              style: { cursor: 'pointer' }
            })}
            columns={[
              {
                title: 'Filename',
                dataIndex: 'filename',
                key: 'filename',
                ellipsis: { showTitle: false },
                render: (filename) => (
                  <Typography.Text ellipsis={{ tooltip: filename }} strong>
                    <FileOutlined style={{ marginRight: 8, color: 'var(--rlc-state-info)' }} />
                    {filename}
                  </Typography.Text>
                ),
              },
              {
                title: 'Type',
                dataIndex: 'file_type',
                key: 'file_type',
                width: 80,
                render: (type) => <Tag>{type?.toUpperCase() || 'FILE'}</Tag>,
              },
              {
                title: 'Size',
                dataIndex: 'file_size',
                key: 'file_size',
                width: 100,
                render: (size) => <Typography.Text type="secondary">{formatFileSize(size)}</Typography.Text>,
              },
              {
                title: 'Status',
                dataIndex: 'processing_status',
                key: 'status',
                width: 150,
                render: (processing_status) => {
                  const status = getProcessingStatus(processing_status);
                  return (
                    <Tag icon={status.icon} color={status.color}>
                      {status.text}
                    </Tag>
                  );
                },
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 150,
                render: (_, doc) => {
                  const menuItems = [
                    {
                      key: 'view',
                      label: 'View',
                      icon: <EyeOutlined />,
                      onClick: () => handleViewFile(doc)
                    }
                  ];

                  if (canDelete) {
                    menuItems.push({
                      key: 'delete',
                      label: 'Delete',
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => openDeleteFileModal(doc)
                    });
                  }

                  return (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Dropdown.Button
                        size="small"
                        type="primary"
                        icon={<DownOutlined />}
                        menu={{ items: menuItems }}
                        onClick={() => handleViewFile(doc)}
                      >
                        View
                      </Dropdown.Button>
                    </div>
                  );
                },
              },
            ]}
          />
        )}
      </Card>

      {/* File Viewer Drawer */}
      {selectedFile && (
        <FileViewerDrawer
          file={selectedFile}
          projectId={projectId}
          allFiles={documents}
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedFile(null);
          }}
          onFileSwitch={handleFileSwitch}
          onDelete={canDelete ? (docId, filename) => {
            // Find the full document object
            const doc = documents.find(d => d._id === docId || d.id === docId);
            if (doc) {
              openDeleteFileModal(doc);
            }
          } : null}
        />
      )}

      {/* Delete Project Confirmation Modal */}
      <Modal
        title="Delete Project"
        open={deleteProjectModalOpen}
        onOk={handleDeleteProject}
        onCancel={() => setDeleteProjectModalOpen(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>Are you sure you want to delete this project and all its files?</Text>
          {project && (
            <Card size="small" style={{ backgroundColor: 'var(--rlc-state-warning-bg)', border: '1px solid var(--rlc-state-warning)' }}>
              <Space direction="vertical" size="small">
                <Text strong>{project.name}</Text>
                <Text type="secondary">{documents.length} file(s) will be permanently deleted</Text>
              </Space>
            </Card>
          )}
          <Text type="danger" strong>This action cannot be undone.</Text>
        </Space>
      </Modal>

      {/* Delete File Confirmation Modal */}
      <Modal
        title="Delete File"
        open={deleteFileModalOpen}
        onOk={handleDeleteFile}
        onCancel={() => {
          setDeleteFileModalOpen(false);
          setFileToDelete(null);
        }}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>Are you sure you want to delete this file?</Text>
          {fileToDelete && (
            <Card size="small" style={{ backgroundColor: 'var(--rlc-state-warning-bg)', border: '1px solid var(--rlc-state-warning)' }}>
              <Space direction="vertical" size="small">
                <Text strong>{fileToDelete.filename}</Text>
                <Text type="secondary">
                  {fileToDelete.file_type?.toUpperCase()} • {(fileToDelete.file_size / 1024).toFixed(1)} KB
                </Text>
              </Space>
            </Card>
          )}
          <Text type="danger" strong>This action cannot be undone.</Text>
        </Space>
      </Modal>
    </div>
  );
};

export default ProjectDetailPage;
